import { createRealtimeSession } from '../api';
import { AUDIO_INACTIVITY_TIMEOUT_MS } from '../config';
import type { LocaleCode } from '../../src/shared/contracts';

export interface RealtimeEventPayload {
  type: string;
  [key: string]: unknown;
}

export interface RealtimeAudioCallbacks {
  onEvent: (event: RealtimeEventPayload) => void;
  onInactivity: () => void;
}

export class RealtimeAudioController {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private localStream: MediaStream | null = null;
  private remoteAudioElement: HTMLAudioElement | null = null;
  private inactivityTimer: number | null = null;
  private lastActivityAt = Date.now();
  private isConnected = false;

  async connect(cardId: string, lang: LocaleCode, callbacks: RealtimeAudioCallbacks): Promise<void> {
    const session = await createRealtimeSession(cardId, lang);
    this.remoteAudioElement = document.createElement('audio');
    this.remoteAudioElement.autoplay = true;
    document.body.append(this.remoteAudioElement);

    this.peerConnection = new RTCPeerConnection();
    this.peerConnection.ontrack = (event) => {
      if (this.remoteAudioElement) {
        this.remoteAudioElement.srcObject = event.streams[0];
      }
    };

    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.peerConnection.addTrack(this.localStream.getTracks()[0], this.localStream);

    this.dataChannel = this.peerConnection.createDataChannel('oai-events');
    this.dataChannel.addEventListener('message', (event) => {
      const payload = JSON.parse(String(event.data)) as RealtimeEventPayload;
      this.markActivity();
      callbacks.onEvent(payload);
    });

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);

    const response = await fetch(`https://api.openai.com/v1/realtime?model=${encodeURIComponent(session.model)}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.clientSecret}`,
        'Content-Type': 'application/sdp',
      },
      body: offer.sdp,
    });

    if (!response.ok) {
      throw new Error(`Realtime connection failed with status ${response.status}`);
    }

    const answer = await response.text();
    await this.peerConnection.setRemoteDescription({ type: 'answer', sdp: answer });

    await this.waitForConnection();
    this.isConnected = true;
    this.startInactivityTimer(callbacks.onInactivity);
  }

  sendTextMessage(message: string): void {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('Realtime data channel is not open.');
    }

    this.markActivity();
    this.dataChannel.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: message }],
        },
      }),
    );
    this.dataChannel.send(
      JSON.stringify({
        type: 'response.create',
        response: { modalities: ['text', 'audio'] },
      }),
    );
  }

  disconnect(): void {
    if (this.inactivityTimer) {
      window.clearInterval(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.dataChannel?.close();
    this.dataChannel = null;
    this.peerConnection?.close();
    this.peerConnection = null;
    this.remoteAudioElement?.remove();
    this.remoteAudioElement = null;
    this.isConnected = false;
  }

  get connected(): boolean {
    return this.isConnected;
  }

  private async waitForConnection(): Promise<void> {
    const peerConnection = this.peerConnection;
    if (!peerConnection) {
      throw new Error('Realtime peer connection is missing.');
    }

    await new Promise<void>((resolve, reject) => {
      const deadline = window.setTimeout(() => reject(new Error('Realtime connection timed out.')), 10_000);
      const check = () => {
        if (!this.peerConnection) {
          window.clearTimeout(deadline);
          reject(new Error('Realtime connection closed.'));
          return;
        }

        if (
          this.peerConnection.iceConnectionState === 'connected' ||
          this.peerConnection.iceConnectionState === 'completed'
        ) {
          window.clearTimeout(deadline);
          resolve();
          return;
        }

        if (['failed', 'disconnected', 'closed'].includes(this.peerConnection.iceConnectionState)) {
          window.clearTimeout(deadline);
          reject(new Error('Realtime connection failed.'));
        }
      };

      peerConnection.addEventListener('iceconnectionstatechange', check);
      check();
    });
  }

  private startInactivityTimer(onInactivity: () => void): void {
    this.lastActivityAt = Date.now();
    this.inactivityTimer = window.setInterval(() => {
      if (Date.now() - this.lastActivityAt >= AUDIO_INACTIVITY_TIMEOUT_MS) {
        onInactivity();
      }
    }, 5_000);
  }

  private markActivity(): void {
    this.lastActivityAt = Date.now();
  }
}
